import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from '../../components/ui/alert';
import { DatePicker } from '../../components/ui/date-picker';
import { 
  Loader2, 
  Package,
  User,
  Boxes,
  Eye,
  Edit,
  AlertTriangle,
  Lock,
  Trash2,
  Plus,
  CheckCircle2,
  Barcode,
  MapPin,
} from 'lucide-react';
import { getStatusInfo } from './constants';

/**
 * View/Edit Order Dialog for Order Pipeline
 */
export default function ViewOrderDialog({
  open,
  onOpenChange,
  selectedOrder,
  editingOrder,
  setEditingOrder,
  orderFormData,
  setOrderFormData,
  customers,
  products,
  cobrandings,
  canEdit,
  canEditOrderFully,
  canEditOrderHeader,
  handleSaveOrder,
  savingOrder,
  userRole
}) {
  const addOrderLine = () => {
    setOrderFormData({
      ...orderFormData,
      lines: [...orderFormData.lines, {
        product_id: '', quantity: '', cases: '', estimated_weight: '',
        notes: '', cobranding_id: '', supplier_product_code: '', use_by_date: ''
      }]
    });
  };

  const updateOrderLine = (index, field, value) => {
    setOrderFormData(prev => {
      const newLines = [...prev.lines];
      newLines[index] = { ...newLines[index], [field]: value };
      return { ...prev, lines: newLines };
    });
  };

  const removeOrderLine = (index) => {
    if (orderFormData.lines.length > 1) {
      setOrderFormData({
        ...orderFormData,
        lines: orderFormData.lines.filter((_, i) => i !== index)
      });
    }
  };

  // Get customer data for delivery address display
  const customer = customers.find(c => c.id === (selectedOrder?.customer_id || orderFormData?.customer_id));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-4xl max-h-[85vh] overflow-y-auto"
        onInteractOutside={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {editingOrder ? <Edit className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            {editingOrder ? 'Edit Order' : 'Order Details'}
            {selectedOrder && (
              <span className="text-sm font-normal text-muted-foreground ml-2">
                #{selectedOrder.dispatch_number}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            {selectedOrder && (
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${getStatusInfo(selectedOrder.status).color}`}>
                {getStatusInfo(selectedOrder.status).label}
              </span>
            )}
            {!canEditOrderFully(selectedOrder) && selectedOrder?.status !== 'draft' && (
              <span className="ml-2 text-xs text-amber-600">
                (Product lines locked - batches created)
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        
        {selectedOrder && (
          <div className="space-y-4 py-2">
            {/* Status indicator for locked lines */}
            {!canEditOrderFully(selectedOrder) && editingOrder && (
              <Alert className="border-amber-200 bg-amber-50">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <AlertTitle className="text-amber-800">Limited Editing</AlertTitle>
                <AlertDescription className="text-amber-700">
                  Product lines are locked because batches have been created. 
                  You can still edit customer, PO number, dates, and notes.
                </AlertDescription>
              </Alert>
            )}
            
            {/* Customer */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <User className="w-4 h-4" />
                Customer
              </Label>
              {editingOrder && canEditOrderHeader(selectedOrder) ? (
                <Select 
                  value={orderFormData.customer_id} 
                  onValueChange={(v) => setOrderFormData({ ...orderFormData, customer_id: v })}
                >
                  <SelectTrigger data-testid="edit-customer-select">
                    <SelectValue placeholder="Select customer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="text-sm font-medium py-2 px-3 bg-muted/30 rounded">
                  {selectedOrder.customer_name}
                </div>
              )}
            </div>

            {/* Delivery Address (view only) */}
            {customer && (customer.delivery_address_line1 || customer.delivery_city) && (
              <div className="flex items-start gap-2 px-3 py-2 bg-muted/20 rounded text-xs text-muted-foreground">
                <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
                <span>
                  {[customer.delivery_address_line1, customer.delivery_address_line2, customer.delivery_city, customer.delivery_county, customer.delivery_postcode, customer.delivery_country].filter(Boolean).join(', ')}
                </span>
              </div>
            )}
            
            {/* PO Number, PO Date, Production Date */}
            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>PO Number</Label>
                {editingOrder && canEditOrderHeader(selectedOrder) ? (
                  <Input
                    value={orderFormData.po_number}
                    onChange={(e) => setOrderFormData({ ...orderFormData, po_number: e.target.value })}
                    placeholder="PO number..."
                    className="font-mono"
                    data-testid="edit-po-number"
                  />
                ) : (
                  <div className="text-sm font-mono py-2 px-3 bg-muted/30 rounded">
                    {selectedOrder.po_number || '-'}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>PO Date</Label>
                {editingOrder && canEditOrderHeader(selectedOrder) ? (
                  <DatePicker
                    value={orderFormData.po_date}
                    onChange={(v) => setOrderFormData({ ...orderFormData, po_date: v })}
                    placeholder="Select date"
                  />
                ) : (
                  <div className="text-sm font-mono py-2 px-3 bg-muted/30 rounded">
                    {selectedOrder.po_date || '-'}
                  </div>
                )}
              </div>
              <div className="space-y-2">
                <Label>Production Date</Label>
                {editingOrder && canEditOrderHeader(selectedOrder) ? (
                  <DatePicker
                    value={orderFormData.dispatch_date}
                    onChange={(v) => setOrderFormData({ ...orderFormData, dispatch_date: v })}
                    placeholder="Select date"
                  />
                ) : (
                  <div className="text-sm font-mono py-2 px-3 bg-muted/30 rounded">
                    {['ready', 'dispatched', 'locked'].includes(selectedOrder.status) 
                      ? (selectedOrder.dispatch_date || '-')
                      : <span className="text-muted-foreground italic">Set during batch creation</span>
                    }
                  </div>
                )}
              </div>
            </div>
            
            {/* Transport */}
            <div className="space-y-2">
              <Label>Transport Agent</Label>
              {editingOrder && canEditOrderHeader(selectedOrder) ? (
                <Input
                  value={orderFormData.vehicle_info}
                  onChange={(e) => setOrderFormData({ ...orderFormData, vehicle_info: e.target.value })}
                  placeholder="Transport agent..."
                  data-testid="edit-transport"
                />
              ) : (
                <div className="text-sm py-2 px-3 bg-muted/30 rounded">
                  {selectedOrder.vehicle_info || '-'}
                </div>
              )}
            </div>
            
            {/* Product Lines */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Package className="w-4 h-4" />
                Product Lines
                {!canEditOrderFully(selectedOrder) && (
                  <Lock className="w-3 h-3 text-muted-foreground" />
                )}
              </Label>
              
              {canEditOrderFully(selectedOrder) && editingOrder ? (
                /* ===== EDIT MODE ===== */
                <div className="space-y-2">
                  {/* Table header */}
                  <div className="grid grid-cols-[1fr_90px_110px_60px_60px_80px_90px_1fr_36px] gap-2 px-3 py-2 bg-muted/50 rounded-t-lg text-xs font-medium text-muted-foreground">
                    <div>Product</div>
                    <div>Customer Code</div>
                    <div>Brand/Pkg</div>
                    <div>Units</div>
                    <div>Cases</div>
                    <div>Wt (kg)</div>
                    <div>Use By</div>
                    <div>Notes</div>
                    <div></div>
                  </div>
                  {orderFormData.lines.map((line, index) => (
                    <div key={index} className="grid grid-cols-[1fr_90px_110px_60px_60px_80px_90px_1fr_36px] gap-2 items-end p-2 bg-muted/20 rounded-lg">
                      <div>
                        <Select value={line.product_id} onValueChange={(v) => {
                          const product = products.find(p => p.id === v);
                          updateOrderLine(index, 'product_id', v);
                          if (product) updateOrderLine(index, 'product_name', product.name);
                        }}>
                          <SelectTrigger className="h-9 text-xs" data-testid={`edit-line-product-${index}`}>
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            {products.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Input
                          value={line.supplier_product_code || ''}
                          onChange={(e) => updateOrderLine(index, 'supplier_product_code', e.target.value)}
                          placeholder="Code..."
                          className="font-mono text-xs h-9"
                          data-testid={`edit-line-supplier-code-${index}`}
                        />
                      </div>
                      <div>
                        <Select 
                          value={line.cobranding_id || 'standard'} 
                          onValueChange={(v) => updateOrderLine(index, 'cobranding_id', v === 'standard' ? '' : v)}
                        >
                          <SelectTrigger className="h-9 text-xs" data-testid={`edit-line-cobranding-${index}`}>
                            <SelectValue placeholder="Standard" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="standard">Standard</SelectItem>
                            {cobrandings.map(cb => (
                              <SelectItem key={cb.id} value={cb.id}>{cb.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Input
                          type="number"
                          value={line.quantity || ''}
                          onChange={(e) => updateOrderLine(index, 'quantity', parseFloat(e.target.value) || '')}
                          placeholder="0"
                          className="font-mono text-xs h-9"
                          data-testid={`edit-line-qty-${index}`}
                        />
                      </div>
                      <div>
                        <Input
                          type="number"
                          value={line.cases || ''}
                          onChange={(e) => updateOrderLine(index, 'cases', e.target.value)}
                          placeholder="0"
                          className="font-mono text-xs h-9"
                          data-testid={`edit-line-cases-${index}`}
                        />
                      </div>
                      <div>
                        <Input
                          type="number"
                          step="0.01"
                          value={line.estimated_weight || ''}
                          onChange={(e) => updateOrderLine(index, 'estimated_weight', e.target.value)}
                          placeholder="0.00"
                          className="font-mono text-xs h-9"
                          data-testid={`edit-line-weight-${index}`}
                        />
                      </div>
                      <div>
                        {line.use_by_date === 'No Date' ? (
                          <div className="h-9 flex items-center px-2 bg-amber-50 border border-amber-200 rounded-md">
                            <span className="text-[10px] font-medium text-amber-700">No Date</span>
                          </div>
                        ) : (
                          <DatePicker
                            value={line.use_by_date || ''}
                            onChange={(v) => updateOrderLine(index, 'use_by_date', v)}
                            placeholder="Date"
                            data-testid={`edit-line-usebydate-${index}`}
                          />
                        )}
                      </div>
                      <div>
                        <Input
                          value={line.notes || ''}
                          onChange={(e) => updateOrderLine(index, 'notes', e.target.value)}
                          placeholder="Notes..."
                          className="text-xs h-9"
                          data-testid={`edit-line-notes-${index}`}
                        />
                      </div>
                      <div className="flex justify-center">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => removeOrderLine(index)}
                          disabled={orderFormData.lines.length === 1}
                          className="h-9 w-9 p-0 text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline" size="sm" onClick={addOrderLine} data-testid="edit-add-line">
                    <Plus className="w-4 h-4 mr-1" />
                    Add Product Line
                  </Button>
                </div>
              ) : (
                /* ===== VIEW MODE ===== */
                <div className="space-y-1">
                  {/* View header */}
                  <div className="grid grid-cols-[1fr_90px_110px_60px_60px_80px_80px_70px_1fr] gap-2 px-3 py-2 bg-muted/50 rounded-t-lg text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                    <div>Product</div>
                    <div>Customer Code</div>
                    <div>Brand/Pkg</div>
                    <div>Units</div>
                    <div>Cases</div>
                    <div>Wt (kg)</div>
                    <div>Use By</div>
                    <div>Batch</div>
                    <div>Notes</div>
                  </div>
                  {selectedOrder.lines?.map((line, index) => {
                    const cobranding = cobrandings.find(c => c.id === line.cobranding_id);
                    const weight = line.total_weight || line.weight_override || line.estimated_weight || 0;
                    const cases = line.cases_override || line.calculated_cases || line.cases || 0;
                    const useByDate = line.use_by_date || '';
                    return (
                      <div key={index} className="grid grid-cols-[1fr_90px_110px_60px_60px_80px_80px_70px_1fr] gap-2 p-2.5 bg-muted/20 rounded-lg text-sm items-center">
                        <div className="font-medium text-xs truncate" title={line.product_name}>
                          {line.product_name}
                        </div>
                        <div className="font-mono text-xs text-muted-foreground truncate" title={line.supplier_product_code}>
                          {line.supplier_product_code || '-'}
                        </div>
                        <div className="text-xs text-muted-foreground truncate">
                          {cobranding?.name || 'Standard'}
                        </div>
                        <div className="font-mono text-xs">
                          {line.quantity}
                        </div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {cases ? parseFloat(cases).toFixed(0) : '-'}
                        </div>
                        <div className="font-mono text-xs text-muted-foreground">
                          {weight ? `${parseFloat(weight).toFixed(2)}` : '-'}
                        </div>
                        <div className={`font-mono text-xs ${useByDate === 'No Date' ? 'text-amber-600 italic' : 'text-muted-foreground'}`}>
                          {useByDate === 'No Date' ? 'No Date' : (useByDate || '-')}
                        </div>
                        <div>
                          {line.batch_code ? (
                            <span className="inline-flex items-center gap-1 text-[11px] text-blue-600 font-mono">
                              <Boxes className="w-3 h-3" />
                              {line.batch_code}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground">-</span>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground truncate" title={line.notes}>
                          {line.notes || '-'}
                        </div>
                      </div>
                    );
                  }) || (
                    <div className="text-center py-4 text-muted-foreground text-sm">
                      No product lines
                    </div>
                  )}
                </div>
              )}
            </div>
            
            {/* Notes */}
            <div className="space-y-2">
              <Label>Notes</Label>
              {editingOrder && canEditOrderHeader(selectedOrder) ? (
                <Textarea
                  value={orderFormData.notes}
                  onChange={(e) => setOrderFormData({ ...orderFormData, notes: e.target.value })}
                  placeholder="Order notes..."
                  rows={2}
                  data-testid="edit-order-notes"
                />
              ) : (
                <div className="text-sm py-2 px-3 bg-muted/30 rounded min-h-[40px]">
                  {selectedOrder.notes || '-'}
                </div>
              )}
            </div>
          </div>
        )}
        
        <DialogFooter>
          {!editingOrder ? (
            <>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Close
              </Button>
              {canEdit(userRole) && canEditOrderHeader(selectedOrder) && (
                <Button onClick={() => setEditingOrder(true)} data-testid="btn-edit-order">
                  <Edit className="w-4 h-4 mr-2" />
                  Edit Order
                </Button>
              )}
            </>
          ) : (
            <>
              <Button variant="outline" onClick={() => setEditingOrder(false)}>
                Cancel
              </Button>
              <Button onClick={handleSaveOrder} disabled={savingOrder} data-testid="btn-save-order">
                {savingOrder ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
